/**
 * Theme Helper Utility
 * Provides dynamic access to current theme colors
 * Uses window.SiteTheme API instead of hardcoded constants
 */

/**
 * Get current theme from SiteTheme API
 * @returns {Object|null} Current theme object or null
 */
export const getCurrentTheme = () => {
    if (typeof window === 'undefined') return null;
    
    // 1. Try SiteTheme API (initialized)
    if (window.SiteTheme) {
        const theme = window.SiteTheme.getTheme();
        if (theme) return theme;
    }

    // 2. Try global SSR data (immediate availability before hydration)
    if (window.__INITIAL_THEME_DATA__) {
        // Return context-specific active theme if available, otherwise activeTheme
        // This matches the logic in SiteTheme initialization
        const { siteActiveTheme, activeTheme } = window.__INITIAL_THEME_DATA__;
        return siteActiveTheme || activeTheme;
    }

    return null;
};

/**
 * Parse theme color key to CSS RGB value
 * Dynamically fetches current theme instead of using hardcoded constant
 * @param {string} colorKey - Color key (e.g., 'primary', 'text')
 * @returns {string} CSS variable reference
 */
export const parseThemeColor = (colorKey) => {
    if (!colorKey) return '';
    
    // Always return CSS variable reference to ensure consistency between SSR and Client
    // This fixes hydration mismatches where SSR uses var() and Client uses raw value
    // The SiteTheme system ensures these variables are defined on :root
    return `var(--color-${colorKey})`;
};

/**
 * Get all theme colors as a map
 * @returns {Object} Map of colorKey -> RGB value
 */
export const getThemeColorsMap = () => {
    const theme = getCurrentTheme();
    if (!theme) return {};

    const colorsMap = {};
    const metadataKeys = ['type', 'name', 'description', 'isDefault', 'isActive', 'createdAt', 'updatedAt', '_id', 'version'];
    
    if (Array.isArray(theme.colors)) {
        theme.colors.forEach(color => {
            if (color.key && color.value) {
                colorsMap[color.key] = color.value;
            }
        });
    } else {
        const colors = theme.colors || theme;
        Object.entries(colors).forEach(([key, val]) => {
            if (typeof val === 'string' && !metadataKeys.includes(key)) {
                colorsMap[key] = val;
            }
        });
    }

    return colorsMap;
};

/**
 * Listen for theme changes
 * @param {Function} callback - Called when theme changes with new theme object
 * @returns {Function} Cleanup function to remove listener
 */
export const onThemeChange = (callback) => {
    if (typeof window === 'undefined') return () => {};

    const handler = (event) => {
        const { theme } = event.detail || {};
        callback(theme);
    };

    window.addEventListener('themeChanged', handler);

    return () => {
        window.removeEventListener('themeChanged', handler);
    };
};
